/**
 * TASK-TMS-004: Placeholder Texture Generators
 *
 * Utility functions to generate placeholder textures for terrain themes.
 * These are used as fallbacks when real theme assets aren't available.
 *
 * Each function creates an HTML canvas element, draws the appropriate
 * pattern/gradient, and returns a PIXI.Texture created from the canvas.
 */

import * as PIXI from 'pixi.js';

/**
 * Creates a solid color texture
 *
 * @param color - Hex color value (e.g., 0xFF0000 for red)
 * @param width - Texture width in pixels
 * @param height - Texture height in pixels
 * @returns PIXI.Texture with solid color
 */
export function createColorTexture(
  color: number,
  width: number,
  height: number
): PIXI.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d')!;

  // Convert hex number to CSS color string
  const colorStr = `#${color.toString(16).padStart(6, '0')}`;
  ctx.fillStyle = colorStr;
  ctx.fillRect(0, 0, width, height);

  return PIXI.Texture.from(canvas);
}

/**
 * Creates a radial gradient texture for fog particles
 * Soft circular gradient from white/gray fading to transparent
 *
 * @param color - Hex color value (typically white 0xFFFFFF)
 * @param width - Texture width in pixels
 * @param height - Texture height in pixels
 * @returns PIXI.Texture with radial gradient
 */
export function createRadialGradientTexture(
  color: number,
  width: number,
  height: number
): PIXI.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d')!;

  // Create radial gradient from center
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = width / 2;

  const gradient = ctx.createRadialGradient(
    centerX, centerY, 0,      // Inner circle (center, radius 0)
    centerX, centerY, radius  // Outer circle (center, radius = width/2)
  );

  // Extract RGB from hex color
  const r = (color >> 16) & 0xFF;
  const g = (color >> 8) & 0xFF;
  const b = color & 0xFF;

  // Gradient from semi-opaque to transparent
  gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.8)`);
  gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  return PIXI.Texture.from(canvas);
}

/**
 * Creates a small bright point texture for dust particles
 *
 * @param color - Hex color value (typically white 0xFFFFFF)
 * @param width - Texture width in pixels
 * @param height - Texture height in pixels
 * @returns PIXI.Texture with point gradient
 */
export function createPointTexture(
  color: number,
  width: number,
  height: number
): PIXI.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d')!;

  // Create tight radial gradient for bright point
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = width / 4; // Smaller radius for tighter point

  const gradient = ctx.createRadialGradient(
    centerX, centerY, 0,
    centerX, centerY, radius
  );

  // Extract RGB from hex color
  const r = (color >> 16) & 0xFF;
  const g = (color >> 8) & 0xFF;
  const b = color & 0xFF;

  // Gradient with 3 stops: full brightness, half, transparent
  gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1)`);
  gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.5)`);
  gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  return PIXI.Texture.from(canvas);
}

/**
 * Creates a grid pattern texture for floor
 * Grid lines on dark background
 *
 * @param color - Hex color value for grid lines (e.g., 0x00D4FF for cyan)
 * @param width - Texture width in pixels
 * @param height - Texture height in pixels
 * @returns PIXI.Texture with grid pattern
 */
export function createGridPatternTexture(
  color: number,
  width: number,
  height: number
): PIXI.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d')!;

  // Dark background (navy blue)
  ctx.fillStyle = '#0a1628';
  ctx.fillRect(0, 0, width, height);

  // Grid lines with transparency
  const colorStr = `#${color.toString(16).padStart(6, '0')}33`; // Add 33 for ~20% opacity
  ctx.strokeStyle = colorStr;
  ctx.lineWidth = 1;

  // Draw vertical grid lines every 8 pixels
  for (let x = 0; x <= width; x += 8) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }

  // Draw horizontal grid lines every 8 pixels
  for (let y = 0; y <= height; y += 8) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  return PIXI.Texture.from(canvas);
}

/**
 * Creates a vertical gradient texture for mid background
 *
 * @param color - Hex color value for top of gradient
 * @param width - Texture width in pixels
 * @param height - Texture height in pixels
 * @returns PIXI.Texture with vertical gradient
 */
export function createGradientTexture(
  color: number,
  width: number,
  height: number
): PIXI.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d')!;

  // Create vertical linear gradient from top to bottom
  const gradient = ctx.createLinearGradient(0, 0, 0, height);

  // Convert hex to CSS color string
  const colorStr = `#${color.toString(16).padStart(6, '0')}`;

  // Gradient from color at top to transparent at bottom
  gradient.addColorStop(0, colorStr);
  gradient.addColorStop(1, 'transparent');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  return PIXI.Texture.from(canvas);
}
